'use client';

import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export interface SmartTradeItem {
  block_time: string;
  maker: string;
  title: string | null;
  token_outcome: string | null;
  group_item_title: string | null;
  side: string | null;
  price: number;
  amount: number;
  shares: number;
}

export interface SmartTradeResponse {
  success: boolean;
  data: SmartTradeItem[];
  message?: string;
  error?: string;
}

export interface FetchSmartTradeParams {
  eventSlug: string;
}

export interface UseSmartTradeOptions extends FetchSmartTradeParams {
  enabled?: boolean;
  swr?: SWRConfiguration<SmartTradeResponse, Error>;
}

export async function fetchSmartTrade({ eventSlug }: FetchSmartTradeParams) {
  const normalizedEventSlug = eventSlug.trim();

  if (!normalizedEventSlug) {
    throw new Error('eventSlug is required');
  }

  const searchParams = new URLSearchParams({
    event_slug: normalizedEventSlug,
  });

  const response = await customFetch(`/api/market/smart-trade?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch smart trade');
  }

  const result = (await response.json()) as SmartTradeResponse;

  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to fetch smart trade');
  }

  return result;
}

export function useSmartTrade(options: UseSmartTradeOptions) {
  const eventSlug = options.eventSlug.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['smart-trade', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<SmartTradeResponse, Error>(
    swrKey,
    () => fetchSmartTrade({ eventSlug }),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    trades: Array.isArray(data?.data) ? data.data : [],
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useSmartTrade;
